import type { RuleDetails } from '@amzn/helianthus-arbiter-client';
import { useSettings } from '../context/ArbiterClientContext';
import { classifyPrincipal } from '../utils/arnUtils';

interface RuleProvenance {
  /** The principal the class was decided from. */
  principal: string | undefined;
  principalClass: ReturnType<typeof classifyPrincipal>;
  /** Which field that principal came from, so the copy can say "created" or "last written". */
  field: 'lastModifiedBy' | 'createdBy';
}

/**
 * Whether a person, the rules registry, or other automation wrote a rule. Both the creator and the last
 * modifier count: a registry rule edited once in the console still gets reverted on the next deploy, and a
 * rule some system created can be re-created by it. Registry outranks other automation, which outranks a
 * person; within a class the last writer is reported.
 */
export function useRuleProvenance(rule: RuleDetails): RuleProvenance {
  const { roleArn } = useSettings();
  const candidates: RuleProvenance[] = (['lastModifiedBy', 'createdBy'] as const)
    .filter((field) => rule[field])
    .map((field) => ({ principal: rule[field], principalClass: classifyPrincipal(rule[field], roleArn), field }));

  return candidates.find((c) => c.principalClass === 'registry')
    ?? candidates.find((c) => c.principalClass === 'automation')
    ?? candidates[0]
    ?? { principal: undefined, principalClass: 'human', field: 'lastModifiedBy' };
}
